import React, { useEffect, useRef, useState } from "react";
import "./index.less";
import {Slider,Select, Spin, InputNumber, Col, Row} from "antd";
// self define
import LightingInvokeButton from "../LightningButton/LightningInvokeButton";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/reducer/store";
import { updateCommonErrorMessage,
    updateCommonWarnMessage,
    updateIsGetAllUSAData,
    updateIsGetCurrentLocation,
    updateIsRefresh,
    updateLocationNameOrCoordinate,
    updateNeedClearLocation,
    updateRadiusValue,
    updateSelectedLocationItem } from "@/redux/actions/lightningAction";
import {getLocations} from "@/util/http/Lightning/index";
import {IGetLocationsResModel} from "@/util/http/Lightning/responseType";
import {debounce} from "lodash";
import { LightningTabsEnum } from "@/util/cosntants";
import type { InputNumberProps } from "antd";
import { LightningCurrentLocation } from "@/resource/IconComponents";

const LightningLocation = () => {
    const dispatch = useDispatch();
    const locationNameOrCoordinate = useSelector((state: RootState) => state.lightningReducer?.locationNameOrCoordinate);
    const radiusValue = useSelector((state: RootState) => state.lightningReducer?.radiusValue);
    const selectedLocationItem = useSelector((state: RootState) => state.lightningReducer?.selectedLocationItem);
    const selectedFavouritesInformation = useSelector((state: RootState) => state.lightningReducer?.selectedFavouritesInfo);
    const needClearLocation=useSelector((state: RootState) => state.lightningReducer?.needClearLocation);
    // 状态管理
    const [isValidValue, setIsValidValue] = useState(true);
    const [locationData, setLocationData] = useState<IGetLocationsResModel[]>([]);
    const [inputValue, setInputValue] = useState<string>(locationNameOrCoordinate || undefined);
    const [searchQuery, setSearchQuery] = useState<string>(""); // 用于实际的搜索查询，更及时反映用户输入
    const [isOpen, setIsOpen] = useState(false); // 控制下拉框显示状态
    const [loading, setLoading] = useState(false); // 加载状态
    const [errorMessage, setErrorMessage] = useState<string | null>(null); // 自定义错误信息

    const [selectKey, setSelectKey] = useState(0); // 新增状态

    const selectRef = useRef<any>();

    // 新增：监听Redux状态变化，同步更新inputValue
    // useEffect(() => {
    //     setInputValue(locationNameOrCoordinate || "");
    // }, [locationNameOrCoordinate]);

    // 半径变化处理
    const changeRadius = (value: number) => {
        dispatch(updateRadiusValue(value));
    };

    // 检查字符串是否包含且只包含一个英文逗号
    const hasSingleComma = (str: string) => {
    // 确保不包含全角逗号
        if (str.includes("，")) return false;

        const commaCount = str.split(",").length - 1;
        return commaCount === 1;
    };

    // 验证经纬度格式
    const isValidCoordinate = (input: string) => {
        if (!hasSingleComma(input)) {
            setErrorMessage("Coordinate format error");
            //"Coordinate format error: should contain exactly one comma"
            return false;
        }

        const splitValue = input.split(",");
        if (splitValue.length !== 2) return false;

        const longitude = parseFloat(splitValue[1].trim());
        const latitude = parseFloat(splitValue[0].trim());

        // 检查是否解析成功
        if (isNaN(longitude) || isNaN(latitude)) {
            setErrorMessage("Invalid coordinate format");
            //"Invalid coordinate format: longitude and latitude must be numbers"
            return false;
        }

        if (longitude < -180 || longitude > 180) {
            setErrorMessage("Longitude must be between -180 and 180");
            return false;
        }

        if (latitude < -90 || latitude > 90) {
            setErrorMessage("Latitude must be between -90 and 90");
            return false;
        }


        setErrorMessage(null);
        dispatch(updateLocationNameOrCoordinate(input));
        dispatch(updateIsGetAllUSAData(false));
        setInputValue(input);
        return true;
    };


    // 验证输入值并决定是否需要搜索 (此函数只做验证，不修改状态)
    const validateInputForSearch = (input: string) => {
        if (input.trim() === "") {
            setErrorMessage("Please enter a location or coordinates");
            return { shouldSearch: false, isValid: false };
        }

        // 如果是经纬度格式
        if (input.includes(",") && hasSingleComma(input)) {
            const validCoord = isValidCoordinate(input);
            setIsValidValue(validCoord);
            return { shouldSearch: false, isValid: validCoord }; // 经纬度格式不触发搜索，只验证
        }

        // 添加：检查是否为纯数字输入
        if (/^-?\d+(\.\d+)?$/.test(input.trim())) {
            setErrorMessage("Please enter a location or coordinates");
            setIsValidValue(false);
            return { shouldSearch: false, isValid: false };
        }

        // 普通地点名称需要搜索
        setErrorMessage(null); // 正常地点名称，清除错误
        setIsValidValue(true);
        return { shouldSearch: true, isValid: true };
    };

    // 防抖处理的搜索函数
    const debouncedFetchLocations = React.useCallback(
        debounce(async (searchText: string) => {
            console.log("lanxue debouncedFetchLocations, searchText:", searchText);

            const { shouldSearch, isValid } = validateInputForSearch(searchText);

            if (!isValid) { // 如果输入无效，不进行搜索
                setIsOpen(false);
                setLocationData([]); // 清空搜索结果
                return;
            }

            if (!shouldSearch) { // 如果是经纬度，不搜索
                setIsOpen(false);
                return;
            }

            setLoading(true);
            setIsOpen(true); // 只有在需要搜索时才打开下拉框

            try {
                const res = await getLocations(searchText);
                setLocationData(res.data || []);
            } catch (error) {
                console.error("Error fetching locations:", error);
                setLocationData([]);
                setErrorMessage("Failed to fetch location data");
            } finally {
                setLoading(false);
            }
        }, 800), // 适当调整防抖时间
        []
    );

    // Select 组件的 onSearch 回调
    const handleSelectSearch = (value: string) => {
        setSearchQuery(value); // 立即更新搜索查询状态，反映用户最新输入
        setInputValue(value); // 让 Select 的 value 始终跟随用户输入
        debouncedFetchLocations(value); // 触发防抖搜索
        dispatch(updateIsGetCurrentLocation(false));
    };


    // 选择选项后的处理
    const handleSelectChange = (value: string) => {
        const tmpLocationData = locationData?.find(info => info.adminName === value);
        // handleInputChange(value);
        setInputValue(value); // 更新 inputValue 为选中的值
        setSearchQuery(value); // 同步 searchQuery
        setIsOpen(false); // 选择后关闭下拉框
        setErrorMessage(null); // 清除错误信息
        dispatch(updateSelectedLocationItem(tmpLocationData));
        dispatch(updateLocationNameOrCoordinate(value)); // 更新 Redux 状态
        dispatch(updateIsGetAllUSAData(false));
        dispatch(updateIsGetCurrentLocation(false));

        // 使用 setTimeout 延迟执行 blur，提高在移动端的成功率
        setTimeout(() => {
            selectRef.current?.blur();
        }, 0);
    };

    // 当输入框失去焦点时，对最终的 inputValue 进行验证
    const handleBlur = () => {
        // 如果当前输入是经纬度格式，确保它被验证过
        if (inputValue && inputValue.includes(",")) {
            // isValidCoordinate(inputValue);
        } else if (inputValue === "") {
            setErrorMessage("Please enter a location or coordinates");
        }
        setIsOpen(false);
    };

    // 当输入框获得焦点时，可能需要根据当前 inputValue 决定是否显示下拉列表
    const handleFocus = () => {
        // 如果当前有搜索结果或者正在加载，可以考虑打开下拉列表
        // 否则如果只是一个经纬度，就不打开
        if (searchQuery.trim() !== "" && locationData.length > 0 && !loading && isValidValue) {
            setIsOpen(true);
        } else if (!searchQuery.includes(",") && searchQuery.trim() !== "") {
            // 如果是地名且有内容，再次触发搜索以便显示结果
            debouncedFetchLocations(searchQuery);
        }
    };


    useEffect(() => {
        // 首次加载或组件初始化时，如果 inputValue 有值，进行一次验证
        // 这对于从外部设置值的情况很有用
        if (inputValue) {
            validateInputForSearch(inputValue);
        }
    }, [inputValue]);


    //open selectedFavouritesInformation
    useEffect(() => {
        if (selectedFavouritesInformation && selectedFavouritesInformation?.options &&
            selectedFavouritesInformation?.options?.currentTab === LightningTabsEnum.LOCATION
        ) {
            setSelectKey(pre => pre + 1); // 更新selectKey以强制重新渲染
            setInputValue(selectedFavouritesInformation?.options?.locationNameOrCoordinate);
            setInputValueNew(selectedFavouritesInformation?.options?.radiusValue || 10);
            dispatch(updateLocationNameOrCoordinate(selectedFavouritesInformation?.options?.locationNameOrCoordinate));
            dispatch(updateIsGetAllUSAData(false));
            dispatch(updateRadiusValue(selectedFavouritesInformation?.options?.radiusValue));
            dispatch(updateSelectedLocationItem(selectedFavouritesInformation?.options?.selectedLocationItem));
            dispatch(updateIsGetCurrentLocation(false)); // 重置为false，表示未获取当前位置
        }
    }, [selectedFavouritesInformation]);

    // 清除处理函数
    const handleClear = () => {
        setSelectKey(pre => pre + 1); // 更新selectKey以强制重新渲染
        // 1. 重置本地状态
        setInputValue(undefined);
        // setIsValidValue(false);
        setIsValidValue(true);
        setIsOpen(false);
        setErrorMessage(null);
        setLoading(false);
        setLocationData([]);
        setInputValueNew(10);

        // 2. 重置Redux状态（关键：确保彻底清空）
        dispatch(updateLocationNameOrCoordinate("")); // 清空经纬度/位置
        dispatch(updateIsGetCurrentLocation(false));
        dispatch(updateSelectedLocationItem(null)); // 清空选中项
        dispatch(updateRadiusValue(10)); // 重置半径为默认值
        dispatch(updateNeedClearLocation(false)); // 重置needClear状态
    };

    // 监听needClear状态，触发清除
    useEffect(() => {
        if (needClearLocation) {
            handleClear();
        }
    }, [needClearLocation]);

    // console.log("lanxue location inputValue:", inputValue);
    // console.log("lanxue location searchQuery:", searchQuery);
    // console.log("lanxue location isValidValue:", isValidValue);
    // console.log("lanxue location loading:", loading);
    // console.log("lanxue location locationData:", locationData);

    const [inputValueNew, setInputValueNew] = useState(10);

    const onChangeRadius: InputNumberProps["onChange"] = (newValue:number) => {
        if (typeof newValue === "number" &&
            !isNaN(newValue) &&
            Number.isInteger(newValue) &&
            newValue >= 1 &&
            newValue <= 20) {
            dispatch(updateIsGetAllUSAData(false));
            setInputValueNew(newValue);
            dispatch(updateRadiusValue(newValue));
        }
    };

    const getCurrentLocation = ()=>{
        try {
            navigator.geolocation.getCurrentPosition(
                (position: GeolocationPosition) => {
                    const pos = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    if(pos?.lat && pos.lng) {
                        // handleMapCenterForCurrent(pos.lat, pos.lng);
                        // const tmpInputValue = "10,20";
                        const tmpInputValue = `${pos.lat},${pos.lng}`;
                        setSelectKey(pre => pre + 1); // 更新selectKey以强制重新渲染
                        setInputValue(tmpInputValue);
                        dispatch(updateLocationNameOrCoordinate(tmpInputValue));
                        dispatch(updateIsGetAllUSAData(false));
                        // dispatch(updateRadiusValue(selectedFavouritesInformation?.options?.radiusValue));
                        dispatch(updateSelectedLocationItem(null));
                        // refresh
                        // dispatch(updateIsRefresh(true));
                        dispatch(updateIsGetCurrentLocation(true)); // 设置为true，表示获取了当前位置
                    }

                },
                (error) => {
                    dispatch(updateCommonWarnMessage("Location not found. Please ensure location services are enabled."));
                }
            );
        } catch (error) {
            dispatch(updateCommonErrorMessage("An unexpected error occurred while fetching your location."));
        }
    };

    return (
        <div className="lightning-location">
            <div className="lightning-location-content">
                {/* Location Search */}
                <div className="lightning-location-content__item">
                    <div className="lightning-location-content__item__location_content">
                        <div className="location-title">Enter Location Or Coordinates (Lat, Long)</div>
                        <div>
                            <Spin spinning={loading}>
                                <div
                                    style={{display:"flex",alignItems:"center",gap:"3px"}}
                                >
                                    <Select ref={selectRef}
                                        key={selectKey} // 使用selectKey强制重新渲染
                                        style={{ width:"310px",maxWidth:"310px",height:"40px"}}
                                        showSearch
                                        placeholder="Latitude, Longitude Or PlaceName"
                                        size="large"
                                        // open={isOpen && isValidValue && !loading} // 只在有效且非加载状态下打开
                                        open={isOpen && isValidValue && (loading || locationData.length > 0 || searchQuery.includes(","))}

                                        // value={inputValue || undefined} // when inputValue is empty,set undefined
                                        value={inputValue || undefined} // when inputValue is empty,set undefined
                                        onChange={handleSelectChange}
                                        onSearch={handleSelectSearch} // 使用防抖搜索
                                        filterOption={false}
                                        loading={loading}
                                        onBlur={handleBlur}
                                        onFocus={handleFocus}
                                    >
                                        {locationData?.map((info) => (
                                            <Select.Option key={info.placeId} value={info.adminName}>
                                                {info?.adminName}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                    <button style={{
                                        backgroundColor: "green",
                                        paddingTop:"2px",
                                        width:"40px",
                                        height:"38px",
                                        cursor: "pointer",
                                        borderRadius:"4px"}}
                                    onClick={()=>{
                                        getCurrentLocation();
                                    }}
                                    >
                                        <LightningCurrentLocation
                                            color={"#FFFFFF"}
                                        />
                                    </button>
                                </div>


                            </Spin>
                            {errorMessage && (
                                <span style={{ color: "red" }}>{errorMessage}</span>
                            )}

                        </div>
                    </div>
                    <div className="lightning-location-content__item__location_radius">
                        <div className="radius-title">Radius in Km (Integer values from 1 to 20)</div>
                        {/* <div className="radius-slider">
                            <Slider
                                className="slider-own"
                                min={1}
                                max={20}

                                onChange={(value) => {
                                    changeRadius(value);
                                }}
                                value={radiusValue}
                            />
                            <div className="radius-value">{radiusValue}</div>
                        </div> */}
                        <div className="radius-slider">
                            <div className="lightning-slider">
                                <Slider
                                    min={1}
                                    max={20}
                                    step={1} // 设置步长为1，确保只能选择整数
                                    onChange={onChangeRadius}
                                    value={inputValueNew}
                                />
                            </div>
                            <div className="lightning-slider-number">
                                <InputNumber
                                    min={1}
                                    max={20}
                                    style={{ margin: "0 16px" }}
                                    value={inputValueNew}
                                    onChange={onChangeRadius}
                                    formatter={value => `${value}`} // 格式化显示
                                    parser={value => value.replace(/[^\d]/g, "")} // 只允许输入数字字符
                                    precision={0} // 限制精度为0位小数
                                />
                            </div>
                        </div>
                    </div>
                </div>

            </div>
            <div className="lightning-location-button">
                <LightingInvokeButton />
            </div>
        </div>
    );
};

export default LightningLocation;